// src/infrastructure/repositories/BlogRepository.ts
import { ConfigManager } from "@/application/config/Config";
import { LoggerFactory } from "@/application/logging/Logger";
import { Blog } from "@/types";

export class BlogRepository {
  private readonly config = ConfigManager.getInstance().getApiConfig();
  private readonly logger = LoggerFactory.getLogger();

  private get authHeader() {
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
    };
  }

  private parseApiResult(json: any): { success: boolean; message: string } {
    const payload = json?.response ?? json;
    const status = Number(payload?.status ?? json?.status ?? 0);
    const message =
      String(payload?.msg ?? json?.msg ?? payload?.message ?? json?.message ?? "").trim() ||
      "Request failed";

    return { success: status === 1, message };
  }

  async uploadBlogImage(userId: number, file: File): Promise<{ image_link: string; file_name: string } | null> {
    const url = `${this.config.baseUrl}/blog/upload_image`;

    try {
      const formData = new FormData();
      formData.append("user_id", String(userId));
      formData.append("image", file);

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("auth_token")}`,
          "X-API-Key": this.config.apiKey,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`uploadBlogImage failed (${response.status})`);
      }

      const json = await response.json();
      const payload = json?.response ?? json;

      if (payload?.status !== 1 || !payload?.image_link) {
        return null;
      }

      return {
        image_link: payload.image_link,
        file_name: payload.file_name ?? "",
      };
    } catch (err) {
      this.logger.error("uploadBlogImage failed", err as Error);
      return null;
    }
  }

// ----------------------------------------
// CREATE BLOG (API: /api/blog/create)
// ----------------------------------------
 async createBlogV2(blog: Blog): Promise<{ success: boolean; message: string }> {
    const url = `${this.config.baseUrl}/blog/create`;

    const payload = {
      title: blog.title,
      slug: blog.slug,
      content: blog.content,
      category_id: blog.category,
      status: "published",

      excerpt: blog.excerpt,
      author: blog.author,
      read_time: blog.readTime,

      image: blog.image,
      image_alt: blog.image_alt ?? "",

      meta_title: blog.meta_title ?? blog.title,
      meta_description: blog.meta_description ?? blog.excerpt,
      meta_keywords: (blog.meta_keywords ?? []).join(","),
      meta_keywords_string: (blog.meta_keywords ?? []).join(","),

      author_image: blog.authorImage ?? "",
      createdBy: String(blog.createdBy ?? 0),
      user_id: String(blog.createdBy ?? 0),
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.authHeader,
        body: JSON.stringify(payload),
      });

      const json = await response.json();
      return this.parseApiResult(json);
    } catch (err) {
      this.logger.error("createBlogV2 failed", err as Error);
      return { success: false, message: "Failed to create blog" };
    }
  }

  // ----------------------------------------
  // UPDATE BLOG (API: /api/blog/update)
  // ----------------------------------------
  async updateBlogV2(blog: Blog): Promise<{ success: boolean; message: string }> {
    const url = `${this.config.baseUrl}/blog/update`;

    const payload = {
      id: String(blog.id),
      title: blog.title,
      slug: blog.slug,
      content: blog.content,
      category_id: blog.category,
      status: blog.status === "draft" ? "No" : "Yes",

      excerpt: blog.excerpt,
      author: blog.author,
      read_time: blog.readTime,

      image: blog.image,
      image_alt: blog.image_alt ?? "",

      meta_title: blog.meta_title ?? blog.title,
      meta_description: blog.meta_description ?? blog.excerpt,
      meta_keywords: (blog.meta_keywords ?? []).join(","),
      meta_keywords_string: (blog.meta_keywords ?? []).join(","),

      author_image: blog.authorImage ?? "",
      createdBy: String(blog.createdBy ?? 0),
      user_id: String(blog.createdBy ?? 0),
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.authHeader,
        body: JSON.stringify(payload),
      });

      const json = await response.json();
      return this.parseApiResult(json);
    } catch (err) {
      this.logger.error("updateBlogV2 failed", err as Error);
      return { success: false, message: "Failed to update blog" };
    }
  }


  // ----------------------------------------
  // CREATE BLOG
  // ----------------------------------------
  async createBlog(payload: Partial<Blog>): Promise<Blog | null> {
    const url = `${this.config.baseUrl}/blogs`;
    
    try {
      const response = await fetch(url, {
        method: "POST",
        headers: this.authHeader,
        body: JSON.stringify(payload),
      });

      const json = await response.json();
      if (json.status !== 1) return null;

      return json.data as Blog;
    } catch (err) {
      this.logger.error("createBlog failed", err as Error);
      return null;
    }
  }

// ----------------------------------------
// GET BLOG LIST (PAGINATED)
// ----------------------------------------
async getBlogList(
  offset: number = 0,
  threshold: number = 10,
  categoryId: string = "19,20"
): Promise<any[]> {
  const url = `${this.config.baseUrl}/blog/list?offset=${offset}&threshold=${threshold}&category_id=${encodeURIComponent(categoryId)}`;

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
       // Authorization: `Bearer ${localStorage.getItem("auth_token")}`,
        accept: "*/*",
      },
    });

    const json = await response.json();

    // API returns { response: [...] }
    return Array.isArray(json.response) ? json.response : [];
  } catch (err) {
    this.logger.error("getBlogList failed", err as Error);
    return [];
  }
}

async getBlogDetail(id: number): Promise<any | null> {
  const url = `${this.config.baseUrl}/blog/detail?id=${id}`;

  try {
    const res = await fetch(url, {
      headers: this.authHeader,
    });
    const json = await res.json();
    return json?.response ?? null;
  } catch {
    return null;
  }
}


  // ----------------------------------------
  // GET MY CREATED BLOGS
  // ----------------------------------------
  async getMyBlogs(userId: number): Promise<Blog[]> {
    const url = `${this.config.baseUrl}/blogs/my/${userId}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: this.authHeader,
      });

      const json = await response.json();
      return json.status === 1 ? json.data : [];
    } catch (err) {
      this.logger.error("getMyBlogs failed", err as Error);
      return [];
    }
  }

  // ----------------------------------------
  // GET PUBLISHED BLOGS
  // ----------------------------------------
  async getPublishedBlogs(): Promise<Blog[]> {
    const url = `${this.config.baseUrl}/blogs/published`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: this.authHeader,
      });

      const json = await response.json();
      return json.status === 1 ? json.data : [];
    } catch (err) {
      this.logger.error("getPublishedBlogs failed", err as Error);
      return [];
    }
  }

  // ----------------------------------------
  // UPDATE BLOG
  // ----------------------------------------
  async updateBlog(blogId: number, payload: Partial<Blog>): Promise<boolean> {
    const url = `${this.config.baseUrl}/blogs/${blogId}`;

    try {
      const response = await fetch(url, {
        method: "PUT",
        headers: this.authHeader,
        body: JSON.stringify(payload),
      });

      const json = await response.json();
      return json.status === 1;
    } catch (err) {
      this.logger.error("updateBlog failed", err as Error);
      return false;
    }
  }

  // ----------------------------------------
  // DELETE BLOG
  // ----------------------------------------
  async deleteBlog(blogId: number): Promise<boolean> {
    const url = `${this.config.baseUrl}/blogs/${blogId}`;

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: this.authHeader,
      });

      const json = await response.json();
      return json.status === 1;
    } catch (err) {
      this.logger.error("deleteBlog failed", err as Error);
      return false;
    }
  }
}
